/**
 * plugins/vuetify.ts
 *
 * Framework documentation: https://vuetifyjs.com`
 */

// Styles
import 'vuetify/styles'
import 'vuetify-sonner/style.css'

// Composables
import { cloneDeep } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { createVuetify, type ThemeDefinition, type VuetifyOptions } from 'vuetify'
import materialSymbols, { materialSymbolsAliases } from './materialSymbolsIconset'
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n'

import { getSubDomainFromCurrentUrl, navigatingOnAvailableSubdomain, SUBDOMAIN_COOKIE_KEY } from '@/common/utils/url'
import store from '@/store'
import { GET_SUBSCRIPTION_BRANDING } from '@/store/modules/subscription/action_types'
import { SET_SUBSCRIPTION_BRANDING } from '@/store/modules/subscription/mutation_types'

import i18n from './i18n'
import { themeColors as DSColors } from '../plugins/styles/colors' // Design system colors

// Colors not in the design system
const colors = {
  brand: '#FF5D35',
  'brand-lighten-1': '#FF6D49',
  'brand-lighten-2': '#FF8E72',
  'brand-lighten-3': '#FFBEAE',
  'brand-lighten-4': '#FFDFD7',
  'brand-lighten-5': '#FFF1EE',
  'brand-darken-1': '#F7552D',
  'brand-darken-2': '#991F00',
  'brand-darken-3': '#661400',
  'brand-darken-4': '#330A00',
  project: '#4286ED',
  'project-lighten-1': '#5593EF',
  'project-lighten-2': '#77ABF9',
  'project-lighten-3': '#B3CFF8',
  'project-lighten-4': '#D9E7FB',
  'project-lighten-5': '#EDF3FE',
  'project-darken-1': '#327DEE',
  'project-darken-2': '#0D408C',
  'project-darken-3': '#092B5D',
  'project-darken-4': '#04152F',
  dataroom: '#803DEC',
  'dataroom-lighten-1': '#8D50EE',
  'dataroom-lighten-2': '#A779F0',
  'dataroom-lighten-3': '#CCB1F7',
  'dataroom-lighten-4': '#E6D8FB',
  'dataroom-lighten-5': '#F3ECFE',
  'dataroom-darken-1': '#782FEE',
  'dataroom-darken-2': '#3E0E8B',
  'dataroom-darken-3': '#29095D',
  'dataroom-darken-4': '#15052E',
  archive: '#1565C0',
  'archive-lighten-1': '#2C74C6',
  'archive-lighten-2': '#5A92D2',
  'archive-lighten-3': '#A1C1E6',
  'archive-lighten-4': '#D0E0F2',
  'archive-lighten-5': '#E8F0F9',
  'archive-darken-1': '#0D60BE',
  'archive-darken-2': '#0F488A',
  'archive-darken-3': '#0A305C',
  'archive-darken-4': '#05182E',
  white: '#FFFFFF',
  grey: '#717B85',
  'grey-lighten-1': '#8D959D',
  'grey-lighten-2': '#AAB0B6',
  'grey-lighten-3': '#AAB0B6',
  'grey-lighten-4': '#E3E5E7',
  'grey-lighten-5': '#F1F2F3',
  'grey-lighten-6': '#FAFAFA',
  'grey-darken-1': '#4F565D',
  'grey-darken-2': '#444A50',
  'grey-darken-3': '#2D3135',
  'grey-darken-4': '#0B0C0D',
  info: '#0095DA',
  'info-lighten-1': '#8D959D',
  'info-lighten-5': '#E9F1FE',
  'info-darken-1': '#2671f2',
  success: '#389B3C',
  warning: '#EB8916',
  'warning-lighten-1': '#FBE7D0',
  error: '#F32E48',
  'error-lighten-1': '#FEEAED',
  'error-darken-1': '#C21930',
  'error-darken-2': '#920C1D',
  'brand-secondary': '#4C75B5',
  background: DSColors['bg-base'],
  secondary: '#4EA9AD',
  tertiary: '#4B75B5',
  tertiaryLight: '#e8ebf0',
  quaternary: '#B99C6B',
  accent: '#333333',
  blue: '#EDF1F8',
  greyDark: '#7a7a7a',
  originalGrey: '#9E9E9E',
  'originalGrey-lighten-1': '#BDBDBD',
  'originalGrey-lighten-2': '#E0E0E0',
  'originalGrey-lighten-3': '#EEEEEE',
  'originalGrey-lighten-4': '#F5F5F5',
  'originalGrey-darken-1': '#757575',
  'originalGrey-darken-2': '#616161',
  'originalGrey-darken-3': '#424242',
  'originalGrey-darken-4': '#212121',
  yellow: '#FFA024',
  'yellow-darken-2': '#995C0E',
  'yellow-lighten-4': '#FFECD3',
  'purple-darken-1': '#7247C9',
  'purple-darken-4': '#311B92',
  'purple-lighten-5': '#F1EBFC',
  'lime-darken-2': '#5A853C',
  'lime-lighten-3': '#D8F1C5',
  'dark-blue-darken-2': '#0C3D91',
  'dark-blue-lighten-4': '#D4E3FC',
}
const textColors = {
  'text-title': DSColors['grey-800'],
  'text-disable': DSColors['grey-300'],
  'text-hyperlink': DSColors['orange-500'],
  'text-reversed': DSColors['white'],
}

const closdTheme: ThemeDefinition = {
  dark: false,
  colors: {
    ...DSColors,
    ...colors,
    ...textColors,
  },
}

function createVuetifyOptions (): VuetifyOptions {
  return {
    defaults: {
      VMenu: {
        openDelay: 0,
        // Vuetify bug: if we set it to 0, in scenarios where the menu activator doesn't
        // have focus (e.g opened by hover), navigating the menu content will first close it.
        // This can cause a lot of issues.
        // @see V2-3668
        closeDelay: 1,
      },
      VDialog: {
        noClickAnimation: true,
        scrim: 'rgba(33,33,33, .46)',
      },
      VOverlay: {
        noClickAnimation: true,
      },
      VCheckbox: {
        color: 'surface-info-bold',
        baseColor: 'icon-secondary',
        density: 'compact',
        ripple: false,
      },
      VCheckboxBtn: {
        color: 'surface-info-bold',
        baseColor: 'icon-secondary',
        density: 'compact',
        ripple: false,
      },
      VRadio: {
        color: 'surface-info-bold',
        baseColor: 'icon-secondary',
      },
      VRadioGroup: {
        color: 'surface-info-bold',
        baseColor: 'icon-secondary',
      },
      VStepperItem: {
        color: 'surface-info-bold',
      },
      VDataTable: {
        density: 'comfortable',
        sortAscIcon: 'arrow_upward',
        sortDescIcon: 'arrow_downward',
      },
      VDataTableServer: {
        density: 'comfortable',
        sortAscIcon: 'arrow_upward',
        sortDescIcon: 'arrow_downward',
      },
      VSwitch: {
        color: 'primary',
      },
      VDivider: {
        color: 'border-secondary',
        opacity: 1,
      },
      VChip: {
        closeIcon: 'close',
      },
      VAlert: {
        closeIcon: 'close',
      },
    },
    theme: {
      defaultTheme: 'closdTheme',
      variations: {
        colors: ['accent', 'primary', 'tertiary', 'quaternary'],
        lighten: 5,
        darken: 4,
      },
      themes: {
        closdTheme,
      },
    },
    locale: {
      // @ts-ignore
      adapter: createVueI18nAdapter({i18n, useI18n}),
    },
    icons: {
      defaultSet: 'materialSymbols',
      aliases: materialSymbolsAliases,
      sets: {
        materialSymbols,
      },
    },
    display: {
      // Specific V2 mobile breakpoint value
      mobileBreakpoint: 1264,
      thresholds: {
        xs: 0,
        sm: 600,
        md: 768,
        lg: 1024,
        xl: 1440,
        xxl: 1600,
      },
    },
  }
}

function createSubscriptionBrandingTheme (): ThemeDefinition | undefined {
  const color = store.getters['subscription/getSubscriptionBrandingColor']
  if (!color) {
    return
  }

  return {
    dark: false,
    colors: {
      ...DSColors,
      ...colors,
      ...textColors,
      primary: color,
    },
  }
}

function applyCustomTheme (options: VuetifyOptions, customTheme: ThemeDefinition, themeName: string): VuetifyOptions {
  // Exclude `locale` and `date` from the deep clone: they contain Vue reactive
  // objects (the i18n.global.locale WritableComputedRef) that cloneDeep would
  // turn into a dead structural copy, breaking Vuetify's locale reactivity.
  // This is new in 2781449, which updated Vuetify to 3.11.9.
  const { locale, date, ...cloneable } = options
  const optionsWithCustomTheme: VuetifyOptions = { ...cloneDeep(cloneable), locale, date }
  ;(optionsWithCustomTheme.theme! as any).themes[themeName] = customTheme
  ;(optionsWithCustomTheme.theme! as any).defaultTheme = themeName

  return optionsWithCustomTheme
}

async function getSubscriptionBranding (subdomain: string) {
  await store.dispatch(`subscription/${GET_SUBSCRIPTION_BRANDING}`, subdomain)
}

export default async function () {
  let vuetifyOptions = createVuetifyOptions()

  /**
   * Vuetify theme color is not correctly customized in App.vue
   * So do it before initializing vuetify to avoid rendering problems
   */
  const subdomain: string = navigatingOnAvailableSubdomain()
    ? getSubDomainFromCurrentUrl()
    : window.$cookies.get(SUBDOMAIN_COOKIE_KEY)

  if (subdomain) {
    // Displays loader for subscription branding loading
    const subscriptionBrandingLoaderId = document.getElementById('pre-init-loader')
    if (subscriptionBrandingLoaderId) {
      subscriptionBrandingLoaderId.style.display = 'flex'
    }

    try {
      await getSubscriptionBranding(subdomain)
      const subscriptionBrandingTheme = createSubscriptionBrandingTheme()
      if (subscriptionBrandingTheme) {
        vuetifyOptions = applyCustomTheme(vuetifyOptions, subscriptionBrandingTheme, 'subscriptionTheme')
      }
    } finally {
      if (subscriptionBrandingLoaderId) {
        subscriptionBrandingLoaderId.style.display = 'none'
      }
    }
  } else {
    // If the user doesn't connect via a subdomain and has customization enabled
    const customizationEnabled = store.getters['user/customizationEnabled']
    if (customizationEnabled) {
      store.commit(`subscription/${SET_SUBSCRIPTION_BRANDING}`, (store.state as any).user?.profile?.subscription)
      const subscriptionBrandingTheme = createSubscriptionBrandingTheme()
      if (subscriptionBrandingTheme) {
        vuetifyOptions = applyCustomTheme(vuetifyOptions, subscriptionBrandingTheme, 'subscriptionTheme')
      }
    }
  }

  return createVuetify(vuetifyOptions)
}
